import React from 'react'
import PropTypes from 'prop-types'
import {FormattedNumber, FormattedMessage} from 'react-intl'
import ItemVariantProvider from '@salesforce/retail-react-app/app/components/item-variant'
import CartItemVariantName from '../../components/item-variant/item-name'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {
    Box,
    Stack,
    Text,
    Flex,
    Skeleton,
    AspectRatio,
    Image,
    Icon
} from '@salesforce/retail-react-app/app/components/shared/ui'
import CartItemVariantAttributes from '../item-variant/item-attributes'
import CartItemVariantImage from '../item-variant/item-image'
import CartItemVariantPrice from '../item-variant/item-price'
import {orderEGiftCardStyle, orderProductStyle} from './products.style'
import PromotionsSection from '../promotions-section'

const onClient = typeof window !== 'undefined'

export const OrderProducts = ({productItems, giftCertificateItems, currency, isLoading}) => {
    const orderProductIds = productItems?.map((product) => product.productId)
    const {data: products, isLoading: isListLoading} = useProducts(
        {
            parameters: {
                ids: orderProductIds,
                locale: 'default'
            }
        },
        {
            enabled: !!orderProductIds && onClient,
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    if (isLoading || (isListLoading && !!orderProductIds)) {
        return [1, 2, 3].map((i) => (
            <Box key={i}>
                <Flex width="full" align="flex-start">
                    <Skeleton boxSize={['88px', 36]} mr={4} />

                    <Stack spacing={2}>
                        <Skeleton h="20px" w="112px" />
                        <Skeleton h="20px" w="84px" />
                        <Skeleton h="20px" w="140px" />
                    </Stack>
                </Flex>
            </Box>
        ))
    }
    const variants = productItems?.map((item) => {
        const product = products?.[item.productId]

        const itemPromotions = Array.isArray(item?.priceAdjustments)
            ? item.priceAdjustments.map((adjustment) => adjustment?.itemText).filter(Boolean)
            : []

        return {
            ...(product ? product : {}),
            isProductUnavailable: !product,
            ...item,
            productPromotions: itemPromotions
        }
    })

    return (
        <>
            {variants?.map((variant, index) => {
                return (
                    <>
                        <Box key={index} sx={orderProductStyle}>
                            <ItemVariantProvider variant={variant} currency={currency}>
                                <Flex width="full" alignItems="flex-start">
                                    <CartItemVariantImage
                                        imageProps={{
                                            height: '100%'
                                        }}
                                        width={['89px', '151px']}
                                        minWidth={['89px', '151px']}
                                        mr={4}
                                    />
                                    <Stack className="product-attr">
                                        <CartItemVariantName />
                                        <Flex
                                            width="full"
                                            justifyContent="space-between"
                                            alignItems="flex-end"
                                        >
                                            <CartItemVariantAttributes
                                                includeQuantity
                                                product={variant}
                                                currency={currency}
                                            />
                                            <CartItemVariantPrice
                                                currency={currency}
                                                align="left"
                                                baseDirection="row"
                                            />
                                        </Flex>
                                        {variant.productPromotions &&
                                            variant.productPromotions.length > 0 && (
                                                <Box mt={2}>
                                                    <PromotionsSection
                                                        promos={variant.productPromotions}
                                                    />
                                                </Box>
                                            )}
                                    </Stack>
                                </Flex>
                            </ItemVariantProvider>
                        </Box>
                        {index < variants?.length - 1 ? <hr /> : null}
                    </>
                )
            })}
            {giftCertificateItems?.map(
                ({recipientEmail, recipientName, senderName, price, name, imageUrl}, ii) => {
                    return (
                        <>
                            {variants?.length ? <hr /> : null}
                            <Stack sx={orderEGiftCardStyle}>
                                <AspectRatio
                                    className="image-container"
                                    ratio={1}
                                    width={['89px', '151px']}
                                    height={['89px', '151px']}
                                    minWidth={['89px', '151px']}
                                >
                                    <Image
                                        objectFit="contain !important"
                                        alt={name}
                                        src={imageUrl}
                                        ignoreFallback={true}
                                    />
                                </AspectRatio>
                                <Stack margin="0 !important">
                                    <Text as="h2">{name}</Text>
                                    <Flex mt="10px" flexDirection="column">
                                        <Text as="span" className="property-item">
                                            <FormattedMessage
                                                defaultMessage="Emailed to: {recipientName}"
                                                id="order_details.physical_gift_cart.email_to"
                                                values={{recipientName}}
                                            />
                                        </Text>
                                        {recipientEmail && (
                                            <Text as="span" className="property-item fullWidth">
                                                {recipientEmail}
                                            </Text>
                                        )}
                                        {senderName && (
                                            <Text as="span" className="property-item fullWidth">
                                                <FormattedMessage
                                                    defaultMessage="From: {senderName}"
                                                    id="order_details.physical_gift_cart.from_sender"
                                                    values={{senderName}}
                                                />
                                            </Text>
                                        )}
                                        <Text as="span">
                                            <FormattedMessage
                                                defaultMessage="Delivery Method: Email Delivery"
                                                id="order_details.physical_gift_cart.delivery_method"
                                            />
                                        </Text>
                                        <Text as="span" mt="10px" color="black !important">
                                            <FormattedNumber
                                                value={price}
                                                style="currency"
                                                currency={currency}
                                            />
                                        </Text>
                                    </Flex>
                                </Stack>
                            </Stack>
                            {ii < giftCertificateItems?.length - 1 ? <hr /> : null}
                        </>
                    )
                }
            )}
        </>
    )
}

OrderProducts.propTypes = {
    productItems: PropTypes.array.isRequired,
    giftCertificateItems: PropTypes.array.isRequired,
    currency: PropTypes.string,
    isLoading: PropTypes.bool
}
